import { FC, ReactNode, SyntheticEvent } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogTitle,
  DialogTitleProps,
  IconButton,
  Stack,
  SxProps,
  Tooltip,
  Typography,
  TypographyProps,
} from '@mui/material';

import XmarkIcon from '@/app/assets/icons/xmark.svg?react';

import classes from './modal-template.module.sass';
import { useTranslation } from 'react-i18next';

interface Props {
  loading?: boolean;
  title?: string;
  submitBtnText?: string;
  cancelBtnText?: string;
  submitBtnColor?: ButtonProps['color'];
  onSubmit?: (event: SyntheticEvent<HTMLDivElement, Event>) => void;
  onClose?: () => void;
  customCancelFunction?: () => void;
  children?: ReactNode;
  TitleProps?: TypographyProps;
  disabled?: boolean;
  noBtns?: boolean;
  hideCancelBtn?: boolean;
  ContainerProps?: BoxProps;
  absoluteCloseBtn?: boolean;
  sx?: SxProps;
  CloseBtnSx?: SxProps;
  DialogTitleProps?: DialogTitleProps;
  actionsTopContent?: ReactNode;
  DialogActionsProps?: DialogActionsProps;
  btnSize?: ButtonProps['size'];
  dialogBottomContent?: ReactNode;
  titleRightContent?: ReactNode;
}

const ModalTemplate: FC<Props> = ({
  loading,
  title,
  onSubmit,
  onClose,
  submitBtnText,
  cancelBtnText,
  children,
  submitBtnColor,
  TitleProps,
  disabled,
  noBtns = false,
  customCancelFunction,
  ContainerProps,
  absoluteCloseBtn,
  CloseBtnSx,
  sx,
  hideCancelBtn,
  DialogTitleProps,
  actionsTopContent,
  DialogActionsProps,
  btnSize,
  dialogBottomContent,
  titleRightContent,
}) => {
  const { t } = useTranslation();

  const handleSubmit = (e: SyntheticEvent<HTMLDivElement, Event>) => {
    e.preventDefault();
    if (onSubmit) onSubmit(e);
  };

  return (
    <Box
      sx={{
        height: '100%',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
      {...(onSubmit && {
        component: 'form',
        onSubmit: handleSubmit,
      })}
    >
      <DialogTitle
        component={'div'}
        sx={{
          p: 3,
          ...DialogTitleProps,
        }}
      >
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          flexDirection='row-reverse'
          sx={{
            width: '100%',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            spacing={1}
          >
            {titleRightContent}
            {onClose && (
              <Tooltip title={t('close')}>
                <IconButton
                  onClick={onClose}
                  sx={{
                    height: '40px',
                    width: '40px',
                    borderRadius: '50%',
                    mr: '-8px',
                    ...(absoluteCloseBtn && {
                      position: 'absolute',
                      right: '20px',
                      mr: 0,
                    }),
                    ...CloseBtnSx,
                  }}
                >
                  <XmarkIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          <Typography
            variant='h4'
            flex={1}
            {...TitleProps}
            sx={{
              lineHeight: 1,
              fontWeight: 500,
              ...TitleProps?.sx,
            }}
            className={classes['title']}
          >
            {title}
          </Typography>
        </Stack>
        {dialogBottomContent}
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
        }}
      >
        <Box
          id='modal-template-body'
          {...ContainerProps}
          sx={{
            ...ContainerProps?.sx,
          }}
        >
          {children}
        </Box>
      </DialogContent>
      {(!noBtns || DialogActionsProps || actionsTopContent) && (
        <DialogActions
          {...DialogActionsProps}
          sx={{
            px: 3,
            pb: 3,
            display: 'block',
            ...DialogActionsProps?.sx,
          }}
        >
          {actionsTopContent}
          {!noBtns && (
            <Stack direction='row' justifyContent='flex-end' spacing={1.25}>
              {onClose && !hideCancelBtn && (
                <Button
                  onClick={
                    customCancelFunction ? customCancelFunction : onClose
                  }
                  color='inherit'
                  disabled={loading}
                  size={btnSize}
                  type='reset'
                >
                  {cancelBtnText || t('cancel')}
                </Button>
              )}
              {onSubmit && (
                <Button
                  type='submit'
                  disabled={disabled || loading}
                  size={btnSize}
                  color={submitBtnColor}
                >
                  {submitBtnText || t('save')}
                </Button>
              )}
            </Stack>
          )}
        </DialogActions>
      )}
    </Box>
  );
};

export default ModalTemplate;
